import { createSelector } from 'reselect';
import { getNormalizedFacility } from './getNearbyFacilities';
import { getFacilityViewPixel } from '@sparefoot/react-core/utils/tracking';
import { get, reduce, omit } from 'lodash';
import { getQueryParams } from 'store/selectors/application';
import { storageStackedUnitBuckets } from 'config/units';
import { getScarcityFlag } from 'utils/store/getScarcityFlag';

export const getFacilityFromRoute = createSelector(
	(state, props) => get(props, 'match.params.facilityId'),
	(state) => state.facilities.byId,
	(facilityId, facilities) => facilities[facilityId],
);

export const getNearbyFacilities = createSelector(
	getFacilityFromRoute,
	(state) => state.facilities.byId,
	(state) => state.units.byId,
	(facility, facilities, units) => {
		// Build the full list of IDs, ensuring current facility is first and not duplicated
		const currentId = facility?.id;
		let ids = [
			currentId,
			...(get(facility, 'nearbyFacilities', []) || []).filter(
				(id) => id !== currentId,
			),
		];
		// Map and normalize all facilities
		const allFacilities = ids.map((facilityId, index) => {
			const fac = facilities[facilityId];
			const facilityUnits = get(fac, 'units', {});
			const unitBuckets = reduce(
				storageStackedUnitBuckets,
				(allUnits, size) => ({
					...allUnits,
					[size]: units[facilityUnits[size]],
				}),
				{},
			);
			return {
				...fac,
				index: index + 1,
				position: index + 1,
				units: {
					...facilityUnits,
					...unitBuckets,
				},
			};
		});
		return allFacilities;
	},
);

export const getAddFacilityData = createSelector(
	(state) => state.app.baseUrl,
	getQueryParams,
	(baseUrl, queryParams) => ({
		submitSuccess: get(queryParams, 'submitSuccess', false),
		canonical: `${baseUrl}/add-facility`,
	}),
);

export default createSelector(
	getFacilityFromRoute,
	getQueryParams,
	getNearbyFacilities,
	(state) => state.facilities.byId,
	(state) => state.facilities.loading,
	(state) => state.facilities.redirectURL,
	(state) => state.facilities.statusCode,
	(state) => state.units.byId,
	(state) => state.reviews.byId,
	(state) => state.app.baseUrl,
	(state) => state.app.cloudfrontUrl,
	(state) => state.experiments,
	(state) => state.facilities.drillDownLinks,
	(
		facility,
		queryParams,
		nearbyFacilities,
		facilities,
		loading,
		redirectURL,
		statusCode,
		units,
		reviews,
		baseUrl,
		cloudfrontUrl,
		experiments,
		drillDownLinks,
	) => {
		const location = get(facility, 'location');
		const groupedAmenities = get(facility, 'groupedAmenities');
		const storageUnits = get(facility, 'units.storageUnits', []).map(
			(unitId) => units[unitId],
		);
		const vehicleUnits = get(facility, 'units.vehicleUnits', []).map(
			(unitId) => units[unitId],
		);

		const isNewSearchPage =
			experiments?.bucket?.sf_16978_ssdc_clp_redesign == 'variation';
		const isGoogleReviewEnabled = get(
			facility,
			'isGoogleReviewEnabled',
			false,
		);

		// Normalize the main facility object
		const normalizedFacility = getNormalizedFacility(facility, units);

		const googleReviews = get(facility, 'googleReviews');

		return {
			facility: normalizedFacility,
			baseUrl,
			cloudfrontUrl,
			nearbyFacilities,
			loading,
			redirectURL,
			statusCode,
			nearbyFacilitiesSearchId: get(facility, 'nearbyFacilitiesSearchId'),
			amenities: omit(
				groupedAmenities,
				!get(facility, 'units.vehicleUnits', []).length
					? ['vehicle']
					: [],
			),
			meta: {
				...get(facility, 'meta', {}),
				title: `${(facility?.meta?.title || '').replace(/:\s*Lowest Rates$/i, '')} | SelfStorage.com`,
				description: `Get the best rate on self storage at ${facility?.name}. Compare prices, read verified reviews, and easily reserve online with SelfStorage.com.`,
				canonical:
					baseUrl +
					(facility?.meta?.canonical || '') +
					(parseInt(get(queryParams, 'reviewPage', 1)) > 1
						? `?reviewPage=${parseInt(get(queryParams, 'reviewPage', 1))}`
						: ''),
			},
			reviewPageNum: parseInt(get(queryParams, 'reviewPage', 1)),
			reviewSource: get(queryParams, 'rsrc'),
			phone: get(facility, 'phone'),
			searchLocation: location
				? `${location.streetAddress1}, ${location.city}, ${location.state} ${location.zip}`
				: '',
			storageUnits,
			vehicleUnits,
			reviews: get(facility, 'userReviews', []).map(
				(reviewId) => reviews[reviewId],
			),
			trackingPixel: getFacilityViewPixel({
				facility: get(facility, 'id'),
			}),
			scarcityShow: getScarcityFlag(units),
			isNewSearchPage,
			isGoogleReviewEnabled,
			googleReviews,
			drillDownLinks,
		};
	},
);
